<template>
  <div 
    v-if="pricingRequest"
    v-show="modalVisible"
    class="overlay overlay--grow"
  > 
    <transition name="slideDown"> 
      <div>

        <div ref="target" class="overlay__inner pricing-alerts-modal" v-show="modalVisible" @click.stop>

          <div class="pricing-row-config">
            <div class="pricing-row-config__head pricing-row-config__head--left pricing-row-config__head--main pricing-row-config__head--drk">
              <button 
                class="btn btn-icon pricing-row-config__close" 
                aria-label="close"
                :disabled="this.isProcessing"
                @click.prevent="closeModal"
              ><i class="fa fa-close"></i></button>

              <h3 class="head__headline"
                @mouseenter="moveable.draggable = true"
                @mouseleave="moveable.draggable = false"
              >Set Alerts</h3> 
            </div>
          </div>

          <div class="pricing-alerts-modal__upper-buttons">
            <div class="upper-buttons__save-btn">
              <button class="btn" 
                @click.prevent="saveRowConfig"
                :disabled="saveButtonIsDisabled"
                :class="{'is-processing': isProcessing}"
              >Save</button> 
              <small v-if="isProcessing">Saving...</small>
            </div>
          </div>

          <div v-if="primaryAccountHolder && additionalUsers.length > 0" class="additional-recipients">
            <label class="form-label">Select Alert recipients in addition to account holder ({{ primaryAccountHolder.firstName }} {{ primaryAccountHolder.lastName }}):</label>

            <div class="additional-recipients__users">

              <div v-for="additionalUser in additionalUsers" class="form-check">
                <input 
                  type="checkbox" 
                  v-model="localAdditionalRecipients"
                  :value="additionalUser.id" 
                  :id="'additional-recipient-' + additionalUser.id" 
                  @change="changesMade = true"
                >
                <label 
                  :for="'additional-recipient-' + additionalUser.id" 
                >{{ additionalUser.firstName }} {{ additionalUser.lastName }}</label>
              </div>

            </div>
          </div>

          <div class="pricing-row-config__existing-price">
            <label class="pricing-row-config__head">Enter an existing Supply price ($/kWh) to display on chart:</label>
            $0.&nbsp;<input 
              type="text" 
              ref="existing-supply-price"
              :value="localExistingSupplyPrice"
              @keyup="updateExistingSupplyPrice"
            >
          </div>

          <div class="pricing-row-config__table">
            <h5 class="pricing-row-config__head">Set Price Alerts</h5>
            <div class="pricing-table pricing-table--outer-border">
              <table cellpadding="0" cellspacing="0" class="pricing-table__table">
                <thead class="pricing-table__table-head">
                  <tr class="pricing-table__row">
                    <th class="pricing-table__cell pricing-table__cell--head pricing-table__cell--xsm">&nbsp;</th>
                    <th class="pricing-table__cell pricing-table__cell--head pricing-table__cell--sm">Term</th>
                    <th class="pricing-table__cell pricing-table__cell--head pricing-table__cell--smed">Component</th>
                    <th class="pricing-table__cell pricing-table__cell--head pricing-table__cell--sm">Trigger</th>
                    <th class="pricing-table__cell pricing-table__cell--head pricing-table__cell--sm">Price<br><small>($/kWh)</small></th>
                    <th class="pricing-table__cell pricing-table__cell--head pricing-table__cell--sm">
                      {{ new Intl.DateTimeFormat('en-US').format(new Date) }} Price<br><small>($/kWh)</small>
                    </th>
                    <th class="pricing-table__cell pricing-table__cell--head pricing-table__cell--xsm">&nbsp;</th>
                  </tr>
                </thead>
                <tbody class="pricing-table__table-body">
                  <template v-if="localPricingAlerts.length">
                    <pricing-alert-row 
                      v-for="(pricingAlert, index) in localPricingAlerts"
                      :key="index"
                      :index="index"
                      :modal-visible="modalVisible"
                      :pricing-data="pricingRequest.data"
                      :pricing-components="pricingComponents"
                      :pricing-alerts="localPricingAlerts"
                      :pricing-request-id="pricingRequest.id"
                      :term-length="pricingRequest.inputs.termLength"
                      :term="pricingAlert.term"
                      :component="pricingAlert.pricing_component_id"
                      :trigger="pricingAlert.trigger"
                      :trigger-price="pricingAlert.price"
                      @removePricingAlert="removePricingAlert"
                      @setTriggerPrice="setPricingAlertTriggerPrice"
                      @setTerm="setPricingAlertTerm"
                      @setComponent="setPricingAlertComponent"
                      @setTrigger="setPricingAlertTrigger"
                    ></pricing-alert-row>
                  </template>
                  <template v-if="!localPricingAlerts.length">
                    <tr class="pricing-table__row">
                      <td colspan="7">
                        <i class="sz-k">No saved pricing alerts</i>
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </div>
          </div> <!-- .pricing-row-config__table -->

          <button class="btn btn-tertiary pricing-row-config__btn-add-alert"
            @click="addPricingAlert"
          ><i class="fa fa-plus"></i>Add Alert</button>


          <template v-if="canAddTimeBasedAlerts">
            <div class="pricing-row-config__table">
              <h5 class="pricing-row-config__head">Set Time-Based Alerts</h5>
              <div class="pricing-table pricing-table--outer-border">
                <table cellpadding="0" cellspacing="0" class="pricing-table__table">
                  <thead class="pricing-table__table-head">
                    <tr class="pricing-table__row">
                      <th class="pricing-table__cell pricing-table__cell--head pricing-table__cell--xsm">&nbsp;</th>
                      <th class="pricing-table__cell pricing-table__cell--head pricing-table__cell--smed">Time Unit</th>
                      <th class="pricing-table__cell pricing-table__cell--head pricing-table__cell--lg">Lead Time</th>
                      <th class="pricing-table__cell pricing-table__cell--head pricing-table__cell--smed">Alert Date</th>
                      <th class="pricing-table__cell pricing-table__cell--head pricing-table__cell--xsm">&nbsp;</th>
                    </tr>
                  </thead>
                  <tbody class="pricing-table__table-body">
                    <template v-if="localTimeBasedAlerts.length">
                      <time-based-alert-row 
                        v-for="(timeBasedAlert, index) in localTimeBasedAlerts"
                        :key="index"
                        :index="index"
                        :id="timeBasedAlert.id"
                        :modal-visible="modalVisible"
                        :pricing-request="pricingRequest"
                        :pricing-request-id="pricingRequest.id"
                        :timeUnit="timeBasedAlert.timeUnit"
                        :leadTime="timeBasedAlert.leadTime"
                        @removeTimeBasedAlert="removeTimeBasedAlert"
                        @setTimeUnit="setTimeBasedAlertTimeUnit"
                        @setLeadTime="setTimeBasedAlertLeadTime"
                      ></time-based-alert-row>
                    </template>
                    <template v-if="!localTimeBasedAlerts.length">
                      <tr class="pricing-table__row">
                        <td colspan="7">
                          <i class="sz-k">No saved time-based alerts</i>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div> <!-- .pricing-row-config__table -->

            <button class="btn btn-tertiary pricing-row-config__btn-add-alert"
              @click="addTimeBasedAlert"
            ><i class="fa fa-plus"></i>Add Alert</button>
          </template>

        </div> <!-- .overlay__inner -->

        <Moveable
          className="moveable"
          :target="$refs.target"
          :draggable="moveable.draggable"
          :origin="false"
          :hideDefaultLines="true"
          @drag="handleDrag"
        />
      </div>
    </transition>
  </div>
</template>

<script>
import { nextTick } from 'vue'
import axios from 'axios'
import Moveable from 'vue3-moveable'
import moment from 'moment-timezone'
import PricingAlertRow from '../../common/PricingAlertRow.vue'
import TimeBasedAlertRow from '../../common/TimeBasedAlertRow.vue'

export default {
  props: ['modalVisible', 'pricingRequestId', 'pricingData'],
  components: {
    'pricing-alert-row': PricingAlertRow,
    'time-based-alert-row': TimeBasedAlertRow,
    Moveable,
  },
  data() {
    return {
      isProcessing: false,
      changesMade: false,
      localPricingAlerts: [],
      localTimeBasedAlerts: [],
      localExistingSupplyPrice: '',
      localAdditionalRecipients: [],
      moveable: {
        draggable: false,
      }
    }
  },
  computed: {
    pricingAlerts() {
      return this.pricingRequest?.pricing_alerts
    },
    timeBasedAlerts() {
      return this.pricingRequest?.time_based_alerts
    },
    pricingComponents() {
      return this.$store.getters.dashboardPricingComponents
    },
    timezone() {
      return this.$store.getters.userTimezone
    },
    currentDate() {
      let utcDate = moment().utc()

      if (this.timezone) {
        return moment(utcDate).tz(this.timezone)
      }

      return moment(utcDate).local()
    },
    canAddTimeBasedAlerts() {
      if (this.pricingRequest) {
        return moment(this.pricingRequest.inputs.startMonth) > this.currentDate.add(1, 'days')
      }
    },
    savedPricingRequests() {
      return this.$store.getters.savedPricingRequests
    },
    pricingRequest() {
      return this.savedPricingRequests.find(pricingRequest => pricingRequest.id === this.pricingRequestId)
    },
    saveButtonIsDisabled() {
      return this.isProcessing || ! this.changesMade
    },
    existingSupplyPrice() {
      if (this.pricingRequest) {
        return this.pricingRequest.inputs.existingSupplyPrice
      }
    },
    /**
     * Get the authenticated user.
     */
    user() {
      return this.$store.getters.user
    },
    /**
     * Get the primary account holder.
     */
    primaryAccountHolder() {
      return this.user?.primaryAccountHolder
    },
    /**
     * Get the primary account holder's additional users.
     */
    additionalUsers() {
      if (this.primaryAccountHolder && this.primaryAccountHolder.additionalUsers) {
        return this.primaryAccountHolder.additionalUsers
      }
      return []
    },
  },
  created() {
    this.populateLocalProperties()

    // If the current user is an additional user and 
    // they created the price request, make sure that 
    // their name is checked by default.
    if (
      this.primaryAccountHolder &&
      this.additionalUsers.length > 0 &&
      this.pricingRequest.creator_info.id == this.user.id &&
      ! this.localAdditionalRecipients.includes(this.user.id)
    ) {
      this.localAdditionalRecipients.push(this.user.id)
      this.changesMade = true
    }
  },
  methods: {
    handleDrag({ target, transform }) {
      let ogTransform = target.style.transform

      target.style.transform = transform

      let bounding = target.getBoundingClientRect();

      if (bounding.top < 0) {
        target.style.transform = ogTransform
      }
    },
    async closeModal() {
      this.$refs.target.setAttribute('style', 'transform: matrix(1,0,0,1,0,0) translate(0, 0)')
      this.showDescription = false
      this.isProcessing = true

      await this.$store.dispatch('fetchSavedPricingRequests')

      this.isProcessing = false
      this.$emit('closeModal')

      this.$store.dispatch('loadSavedPricingRequest', {id: this.pricingRequestId})

      this.populateLocalProperties()
    },
    updateExistingSupplyPrice(event) {
      let val = event.target.value.replace(/\D/g,'')

      this.$refs['existing-supply-price'].value = val

      this.localExistingSupplyPrice = val
      this.changesMade = true
    },
    addPricingAlert() {
      this.localPricingAlerts.push({
        term: '',
        component: '',
        trigger: '',
        price: '',
      })

      this.changesMade = true
    },
    removePricingAlert(index) {
      let targetId = this.localPricingAlerts[index].id

      let swalContent = document.createElement('div');
      swalContent.innerHTML = '<p class="alerts message alerts--no-brdr alerts--close-btn">Are you sure you want to delete this price alert?</p>';

      swal({
        content: swalContent,
        closeOnEsc: false,
        closeOnClickOutside: false,
        buttons: {
          cancel: {
            text: "Cancel",
            value: null,
            visible: true,
            className: "",
            closeModal: true,
          },
          confirm: {
            text: "Yes",
            value: true,
            visible: true,
            className: "",
            closeModal: true
          }
        }
      }).then(result => {
        if (result) {
          if (! targetId) {
            this.localPricingAlerts.splice(index, 1)

          } else {
            axios.delete('/pricing-calculator/me/pricing-alerts/' + targetId)
              .then(response => {
                this.localPricingAlerts.splice(index, 1)
              })
          }
        }
      })
    },
    addTimeBasedAlert() {
      this.localTimeBasedAlerts.push({
        timeUnit: 'days',
        leadTime: null
      })

      this.changesMade = true

      nextTick(() => {
        let scrollableHeight = this.$refs[this.scrollableRef].clientHeight

        if (this.$refs[this.modalRef]) {
          this.$refs[this.modalRef].scrollTo({ top: scrollableHeight, behavior: 'smooth' })
        }
      })
    },
    removeTimeBasedAlert(index) {
      let targetId = this.localTimeBasedAlerts[index].id

      let swalContent = document.createElement('div');
      swalContent.innerHTML = '<p class="alerts message alerts--no-brdr alerts--close-btn">Are you sure you want to delete this time-based alert?</p>';

      swal({
        content: swalContent,
        closeOnEsc: false,
        closeOnClickOutside: false,
        buttons: {
          cancel: {
            text: "Cancel",
            value: null,
            visible: true,
            className: "",
            closeModal: true,
          },
          confirm: {
            text: "Yes",
            value: true,
            visible: true,
            className: "",
            closeModal: true
          }
        }
      }).then(result => {
        if (result) {
          if (! targetId) {
            this.localTimeBasedAlerts.splice(index, 1)

          } else {
            axios.delete('/pricing-calculator/me/time-based-alerts/' + targetId)
              .then(response => {
                this.localTimeBasedAlerts.splice(index, 1)
              })
          }
        }
      })
    },
    setPricingAlertTriggerPrice(index, val) {
      this.localPricingAlerts[index].price = val
      this.changesMade = true
    },
    setPricingAlertTerm(index, term) {
      this.localPricingAlerts[index].term = term
      this.changesMade = true
    },
    setPricingAlertComponent(index, component) {
      this.localPricingAlerts[index].pricing_component_id = component
      this.changesMade = true
    },
    setPricingAlertTrigger(index, trigger) {
      this.localPricingAlerts[index].trigger = trigger
      this.changesMade = true
    },
    setTimeBasedAlertTimeUnit(index, timeUnit) {
      this.localTimeBasedAlerts[index].timeUnit = timeUnit
      this.changesMade = true
    },
    setTimeBasedAlertLeadTime(index, leadTime) {
      this.localTimeBasedAlerts[index].leadTime = leadTime
      this.changesMade = true
    },
    async saveRowConfig() {
      this.isProcessing = true

      let alert
      let pricingAlertsPayload = {alerts: []}
      let timeBasedAlertsPayload = {alerts: []}

      // Pricing Alerts
      for (let i = 0; i < this.localPricingAlerts.length; ++i) {
        alert = {
          term: this.localPricingAlerts[i].term,
          trigger: this.localPricingAlerts[i].trigger.toLowerCase(),
          price: (('0.' + this.localPricingAlerts[i].price.replace('0.', '')) * 1)
        }

        if (this.localPricingAlerts[i].pricing_component_id) {
          // Add the ID for any component except total.
          alert.pricing_component_id = this.localPricingAlerts[i].pricing_component_id
        }

        if (this.localPricingAlerts[i].id) {
          // We're updating an alert, which requires an alert id.
          alert.id = this.localPricingAlerts[i].id
          pricingAlertsPayload.alerts.push(alert)

        } else {
          // We're adding an alert, which requires a pricing request id.
          alert.user_inputs_id = this.pricingRequestId
          pricingAlertsPayload.alerts.push(alert)
        }
      }

      // Time-Based Alerts
      for (let i = 0; i < this.localTimeBasedAlerts.length; ++i) {
        alert = {
          time_unit: this.localTimeBasedAlerts[i].timeUnit,
          lead_time: this.localTimeBasedAlerts[i].leadTime
        }

        if (this.localTimeBasedAlerts[i].id) {
          // We're updating an alert, which requires an alert id.
          alert.id = this.localTimeBasedAlerts[i].id
          timeBasedAlertsPayload.alerts.push(alert)

        } else {
          // We're adding an alert, which requires a pricing request id.
          alert.user_inputs_id = this.pricingRequestId
          timeBasedAlertsPayload.alerts.push(alert)
        }
      }

      // Upsert pricing alerts.
      if (pricingAlertsPayload.alerts.length) {
        try {
          await axios.put('/pricing-calculator/me/pricing-alerts', pricingAlertsPayload)
        } catch (error) {
          this.isProcessing = false
          swal('', 'An unexpected error occurred while saving the pricing alerts. Please ensure all fields are completed.', 'error')
          return
        }
      }

      // Upsert time-based alerts.
      if (timeBasedAlertsPayload.alerts.length) {
        try {
          await axios.put('/pricing-calculator/me/time-based-alerts', timeBasedAlertsPayload)
        } catch (error) {
          this.isProcessing = false
          swal('', 'An unexpected error occurred while saving the time-based alerts. Please ensure all fields are completed.', 'error')
          return
        }
      }

      // Save the additional recipients and existing supply price.
      this.$store.dispatch('updatePricingRequestAlertsConfig', {
        id: this.pricingRequestId,
        additionalRecipients: this.localAdditionalRecipients,

        // Convert to float, since that's the actual value (it's only inputted as an integer).
        existingSupplyPrice: (('0.' + this.localExistingSupplyPrice.toString().replace('0.', '')) * 1),
      })
      .then(() => {

        // We need to wait for a half a second to give the server cache time to 
        // forget the old pricing component data.
        setTimeout(() => {
          this.closeModal()
        }, 500)
      })
    },
    populateLocalProperties() {
      this.localPricingAlerts = []
      this.localTimeBasedAlerts = []
      this.localAdditionalRecipients = []

      // Additional recipients
      this.localAdditionalRecipients = [...this.pricingRequest?.inputs?.additionalAlertRecipients || []]

      // Convert to an integer since that's how it's inputted.
      if (this.existingSupplyPrice) {
        this.localExistingSupplyPrice = this.existingSupplyPrice.toString().replace('0.', '')
      }

      // Pricing alerts
      if (this.pricingAlerts) {
        let alerts = _.cloneDeep(this.pricingAlerts)
        for (let i = 0; i < alerts.length; ++i) {
          this.localPricingAlerts.push(alerts[i])
        }
      }

      // Time-based alerts
      if (this.timeBasedAlerts) {
        let alerts = _.cloneDeep(this.timeBasedAlerts)
        for (let i = 0; i < alerts.length; ++i) {
          this.localTimeBasedAlerts.push({
            ...alerts[i], 
            ...{
              timeUnit: alerts[i].time_unit,
              leadTime: alerts[i].lead_time,
            }
          })
        }
      }
    },
  },
}
</script>
